import React from 'react';
import cn from 'classnames';

import { Typography } from 'components/shared/Typography';

import { PreviewPlaceholder } from 'components/PreviewPlaceholder';

import { ConnectPoint } from '../ConnectPoint';

import styles from './Item.module.scss';

type AnswersType = 'TEXT_TEXT' | 'IMAGE_TEXT' | 'IMAGE_IMAGE';

type ItemSide = 'LEFT_ITEM' | 'RIGHT_ITEM';

export type ItemClickPayload = {
  event: React.MouseEvent;
  id: string;
  pointRef: React.RefObject<HTMLDivElement | null>;
  type: ItemSide;
  pointUsed?: boolean;
};

type RenderContentArgs = {
  itemType: ItemSide;
  answersType: AnswersType;
  content?: string | null;
};

const _renderContent = ({
  itemType,
  answersType,
  content,
}: RenderContentArgs) => {
  switch (answersType) {
    case 'TEXT_TEXT': {
      return (
        <Typography className={styles.text} variant="body2" mode="compact">
          {content}
        </Typography>
      );
    }
    case 'IMAGE_TEXT': {
      if (itemType === 'LEFT_ITEM') {
        return content ? (
          <img className={styles.img} src={content} alt="" />
        ) : (
          <PreviewPlaceholder variant="connectionQuiz" />
        );
      }

      if (itemType === 'RIGHT_ITEM') {
        return (
          <Typography className={styles.text} variant="body2" mode="compact">
            {content}
          </Typography>
        );
      }

      return null;
    }
    case 'IMAGE_IMAGE': {
      return content ? (
        <img className={styles.img} src={content} alt="" />
      ) : (
        <PreviewPlaceholder variant="connectionQuiz" />
      );
    }
    default: {
      return null;
    }
  }
};

type RenderItemArgs = {
  className?: string;
  lineDrawing?: boolean;
  linesValidated?: boolean;
  correctLine?: boolean;
  pointRef: React.RefObject<HTMLDivElement | null>;
  pointUsed?: boolean;
  id: string;
  type: ItemSide;
  answersType: AnswersType;
  content?: string | null;
  onClick: (payload: ItemClickPayload) => void;
  isTestMode?: boolean;
};

const _renderItem = ({
  className,
  lineDrawing,
  linesValidated,
  correctLine,
  pointRef,
  pointUsed,
  id,
  type,
  answersType,
  content,
  onClick,
  isTestMode,
}: RenderItemArgs) => (
  <div
    className={cn(
      styles.item,
      {
        [styles.readOnlyItem]: !isTestMode,
        [styles.itemSuccess]: linesValidated && correctLine,
        [styles.itemError]: linesValidated && !correctLine,
        [styles.itemText]:
          answersType === 'IMAGE_TEXT' && type === 'RIGHT_ITEM',
        [styles.itemImage]:
          (answersType === 'IMAGE_TEXT' && type === 'LEFT_ITEM') ||
          answersType === 'IMAGE_IMAGE',
      },
      className,
    )}
    onClick={(event: any) => onClick({ event, id, pointRef, type, pointUsed })}
  >
    {_renderContent({ itemType: type, answersType, content })}
    <ConnectPoint
      lineDrawing={lineDrawing}
      linesValidated={linesValidated}
      correctLine={correctLine}
      rootRef={pointRef}
      id={id}
      pointUsed={pointUsed}
      type={type}
    />
  </div>
);

type ItemProps = RenderItemArgs;

const Item = ({ answersType, type, ...props }: ItemProps) => {
  if (answersType === 'IMAGE_TEXT' && type === 'RIGHT_ITEM') {
    return (
      <div className={styles.itemWrapper}>
        {_renderItem({
          answersType,
          type,
          ...props,
        })}
      </div>
    );
  }

  return _renderItem({
    answersType,
    type,
    ...props,
  });
};

export default React.memo(Item);
